// live/preflopView.ts (PF4) - the preflop analogue of liveView.ts. Adapts a live engine PreflopSnapshot
// (PF3, engine/liveRunner) to the SAME props the reused S18 sub-components render (ReadLockIn / MathAid /
// BetMathBuilder / GradeReveal), so the DO-mode surfaces open on a playable preflop decision using the
// locked design system verbatim. It carries NO strategy: the option set is the true legal set relabeled
// with preflop intents (limp / open / 3-bet / 4-bet - never a template), the grade is snap.grade(user)
// (PF2's real chart+EV grade), and the coach text is built $0 from the PreflopRecommendation the engine
// already produced (targetStatement + fired-rule principles + chart provenance - no API call).
//
// Hero is pinned to the Button in the live spec (liveRunner.baseSpec), so preflop is always an OPEN
// decision (folded/limped to hero → fold / limp / open) or a DEFEND decision (a villain opened → fold /
// call / 3-bet, then vs-3bet → 4-bet). The label mapping keys off recommendation.role, which names the
// node deterministically. Design signed off by Joe 2026-07-14 (small preflop-ramp gate, BUILD_PLAN §PF4).
import type { ActionKind, PreflopIntent, PreflopRecommendation } from '@postflop/schema';
import type { BetMathDef, PriceDef, PlayerTag, QuickSize } from '../diagnose/data';
import type { RampAction } from '../engine/liveHand';
import { MOVE_CLASS_FROM_BAND, type MoveClass, type ResolvedGrade } from '../grade/types';
import { money, POSITION_LABEL } from '../engine/bridge';
import { displayName, type PreflopSnapshot, type LiveHandModel } from '../engine/liveRunner';
import { actionLabel, gradeReadAxis } from './liveView';

const round1 = (n: number): number => Math.round(n * 10) / 10;

// ── the node's raise DEPTH - what the pot itself says this decision is called ────────────────────────────

/**
 * How many raises are already in this preflop pot, i.e. hero's DEPTH at this node.
 *
 * The big blind is the bet, so an open is the 2-bet: with `n` raises in, the bet hero FACES is the
 * (n+1)-bet and a raise of his own would be the (n+2)-bet. n=0 ⇒ he Opens; n=1 ⇒ he 3-bets the open;
 * n=2 ⇒ he 4-bets the 3-bet; n=3 ⇒ he 5-bets.
 *
 * WHY NOT `recommendation.role`, which this file used to key every label on: the engine's role names which
 * CHART resolves hero's combo, not what his raise is called. `deriveHeroPreflopSpot` (engine
 * strategy/heroSpot.ts:68-79) maps a non-opener facing a re-raise back to 'defend' vs the FIRST raiser -
 * right for the chart, since a squeezer facing a 4-bet is still resolved against the opener's range, but it
 * means role 'defend' covers hero's 3-bet AND his 5-bet. That was invisible while only hero's first preflop
 * node was ever rendered; FULL's re-open makes the later nodes playable, and a button reading "3-bet /
 * re-raise the open" while hero stares at a $144 4-bet is simply a lie about the hand he is in.
 *
 * Only the LABELS read this. The grade still speaks the engine's dialect (`intentFromRamp` → `s.grade`),
 * because that is the vocabulary the PF2 grader compares against `recommendation.finalIntent`.
 *
 * Mirrors `isRaise` in engine strategy/heroSpot.ts: a jam only counts as a raise when it exceeds the bb.
 */
const raisesIn = (s: PreflopSnapshot): number => {
  const bb = s.state.spec.stakes.bb;
  return s.state.history.filter(
    (h) =>
      h.street === 'preflop' &&
      (h.action.kind === 'raise' || (h.action.kind === 'jam' && (h.action.amount ?? 0) > bb)),
  ).length;
};

/** What HERO's raise is called at depth `n` (he would be making the (n+2)-bet). */
const raiseFor = (n: number): { label: string; sub: string } =>
  n === 0
    ? { label: 'Open', sub: 'raise it up, first in' }
    : n === 1
      ? { label: '3-bet', sub: 're-raise the open' }
      : n === 2
        ? { label: '4-bet', sub: 're-raise the 3-bet' }
        : n === 3
          ? { label: '5-bet', sub: 're-raise the 4-bet' }
          : { label: 'Re-raise', sub: 'put it in again' };

/** What hero FACES at depth `n` - the (n+1)-bet. ('the big blind' at n=0: nobody has raised.) */
const facing = (n: number): string =>
  n === 0 ? 'the big blind' : n === 1 ? 'the open' : n === 2 ? 'the 3-bet' : n === 3 ? 'the 4-bet' : 'the 5-bet';

/** The VERB for the last raise made - how the pot got to this node ("opens to $9", "4-bets to $144"). */
const facingVerb = (n: number): string =>
  n === 1 ? 'opens' : n === 2 ? '3-bets' : n === 3 ? '4-bets' : n === 4 ? '5-bets' : 're-raises';

// ── the preflop-labeled ramp (limp / call / open / 3-bet / 4-bet - off the legal set + node depth) ───────

/** Relabel the true legal ramp (RampAction[], mechanical kinds) with preflop intents. Driven ENTIRELY by
 *  the legal set + the node role - never a fixed template. fold/check/jam are role-invariant; call and
 *  raise carry the node's semantic label. */
export const preflopRamp = (s: PreflopSnapshot): RampAction[] => {
  const n = raisesIn(s);
  return s.ramp.map((a): RampAction => {
    switch (a.key) {
      case 'fold':
        return { ...a, label: 'Fold', sub: 'give it up' };
      case 'check':
        return { ...a, label: 'Check', sub: 'take the free flop' };
      case 'call': {
        // Unraised (folded/limped to hero) calling the big blind IS a limp - the toCall is just the BB, not
        // a raise. Facing any real raise it is a Call of that raise, named for what it actually is.
        const label = n === 0 ? 'Limp' : `Call ${money(s.price.toCall)}`;
        return { ...a, label, sub: n === 0 ? 'just call the big blind' : `call ${facing(n)}` };
      }
      case 'bet':
      case 'raise': {
        const r = raiseFor(n);
        return { ...a, label: r.label, sub: r.sub, cls: 'raise' };
      }
      case 'jam':
        return { ...a, label: 'All in', sub: 'shove the stack' };
    }
  });
};

// ── the manual bet-math tier for preflop (bb-denominated sizing conventions, nothing pre-filled) ─────────

/** Quick-size presets for the raise, sized off the poker convention for the node and clamped to the
 *  engine's legal min/max. Opens are quoted in big blinds (2.5bb / 3bb / iso); 3-bets and 4-bets are
 *  multiples of the bet faced. NOTHING pre-fills the input (BetMathBuilder starts empty); these chips only
 *  fill the field on tap, exactly like the postflop tier. Null when there is no raise option. */
export const preflopBetDef = (s: PreflopSnapshot): BetMathDef | null => {
  const raise = s.ramp.find((r) => r.key === 'raise' || r.key === 'bet');
  if (!raise) return null;
  const bb = s.state.spec.stakes.bb;
  const n = raisesIn(s);
  const { pot, toCall } = s.price;
  const currentBet = s.state.currentBet;
  const clamp = (raw: number): number =>
    Math.max(raise.min ?? Math.round(raw), Math.min(raise.max ?? Math.round(raw), Math.round(raw)));
  // the presets are multiples of the bet faced, clamped to the engine's legal band. Deep in a re-raised pot
  // the band collapses toward hero's stack and every multiple clamps to the same number, so identical chips
  // are dropped rather than shown twice - at that point the only real question is "all of it or not".
  const multiples = (ms: Array<{ m: number; r: string }>): QuickSize[] => {
    const seen = new Set<string>();
    return ms
      .map(({ m, r }) => ({ amount: money(clamp(m * currentBet)), ratio: r }))
      .filter((q) => (seen.has(q.amount) ? false : (seen.add(q.amount), true)));
  };

  let pfx: string;
  let sub: string;
  let quick: QuickSize[];
  if (n === 0) {
    pfx = 'Open to';
    sub = 'Opens are quoted in big blinds. On a loose table with limpers, size UP to isolate - a small raise gets five callers.';
    const opens: Array<{ b: number; r: string }> = [
      { b: 2.5, r: '2.5bb' },
      { b: 3, r: '3bb' },
    ];
    const target = s.recommendation.sizing?.toBB;
    if (target && target > 3.2) opens.push({ b: target, r: `iso ${round1(target)}bb` });
    quick = opens.map(({ b, r }) => ({ amount: money(clamp(b * bb)), ratio: r }));
  } else if (n === 1) {
    pfx = '3-bet to';
    sub = 'A 3-bet is sized off the open - about 3x it in position, 4x out. Build the number, do not guess it.';
    quick = multiples([
      { m: 3, r: '3x' },
      { m: 4, r: '4x' },
    ]);
  } else if (n === 2) {
    pfx = '4-bet to';
    sub = 'A 4-bet is small - about 2.2x to 2.5x the 3-bet. You are not folding out much that beats you.';
    quick = multiples([
      { m: 2.2, r: '2.2x' },
      { m: 2.5, r: '2.5x' },
    ]);
  } else {
    // 5-bet and beyond: the stacks are the sizing. Nobody re-raises a 4-bet as a "2.2x" - the pot is already
    // laying a price that commits you, so the number to build is the one you are willing to get in for.
    pfx = 'Raise to';
    sub = `Re-raising ${facing(n)} this deep is a stacking-off decision, not a sizing one - the pot is already big enough that raising commits you. Build the number you are happy to play for.`;
    quick = multiples([
      { m: 2, r: '2x' },
      { m: 2.2, r: '2.2x' },
    ]);
  }

  return {
    pfx,
    pot: money(pot),
    call: toCall > 0 ? money(toCall) : 'none',
    callLabel: 'To call',
    vbet: toCall > 0 ? money(toCall) : 'limps',
    sub,
    quick,
  };
};

// ── the app-shown price (MathAid): the price FACT at this node - "No raise"/"Free" when hero is first in,
// required-equity % when he faces a real raise.

/**
 * THIS PANEL RENDERS BEFORE HERO ACTS, SO IT MUST NEVER NAME AN ACTION IT CANNOT KNOW.
 *
 * It takes a `PreflopSnapshot`, which carries `s.recommendation` - and wiring `recommendation.finalIntent`
 * in here is the obvious "fix" and is WRONG. This panel is the app-shown MathAid ("This one the app shows
 * you"); showing the engine's answer in it hands the user the intent before he picks one, which breaks
 * do-your-own-math (North Star §4.2 - "pick the intent, build the number"). The engine's answer belongs in
 * the debrief, after he has committed.
 *
 * The escape is that everything here is a fact about the POT, true for 53o and AA alike: how many raises
 * are in, what it costs to call, what a raise of a given size would do. Never "your plan is X".
 *
 * (It used to say `title: 'The plan'` + big word "Open" at every unraised node - generic node coaching that
 * read as personalized advice, recommending an open for junk the chart folds. Reworded 2026-07-15, wording
 * signed off by Joe; the parallel with the facing-a-raise branch below - both titled 'The price' - is the
 * point, not an accident.)
 */
export const preflopPrice = (s: PreflopSnapshot): PriceDef => {
  const role = s.recommendation.role;
  const n = raisesIn(s);
  const { pot, toCall } = s.price;
  // OPEN node: hero is first in. `toCall` is only the big blind, NOT a raise to price - so there is no
  // pot-odds decision to work out, which is itself the price fact worth stating. (In the BB with everyone
  // limping, toCall is 0 and the flop is literally free.)
  if (role === 'open') {
    const opening = toCall > 0;
    return {
      title: 'The price',
      big: opening ? 'No raise' : 'Free',
      full: opening
        ? [
            'Nobody has raised - there is no price to call, so there are no pot odds to work out here. What a raise from this seat does is ',
            { term: 'isolate', text: 'isolate' },
            ': about 3 big blinds plus one per limper, sized to get one caller rather than five.',
          ]
        : [
            'Nobody has raised and your blind is already in, so this flop is free. The only reason to put more in is to build a pot.',
          ],
      shorthand: opening
        ? 'No raise in front, so no price. An iso raise is about 3 big blinds plus one per limper.'
        : 'No bet to face, so the flop is free.',
    };
  }
  // hero faces a real raise - now there IS a price to call it. Named for the raise actually in front of him
  // (the open, the 3-bet, the 4-bet …), which is not always the open once he can be re-raised.
  const potAfter = pot + toCall;
  const reqEq = Math.round((toCall / potAfter) * 100);
  return {
    title: 'The price',
    big: `${reqEq}%`,
    full: [
      `${facing(n)[0].toUpperCase()}${facing(n).slice(1)} is ${money(toCall)} to you into ${money(pot)}. Call and you play for a ${money(potAfter)} pot, so you need to be ahead about `,
      { term: 'reqeq', text: 'required equity' },
      ` ${reqEq}% - and preflop you also have position and the streets to come.`,
    ],
    shorthand: `${money(toCall)} to win a ${money(potAfter)} pot. ${toCall} / ${potAfter} is about ${reqEq}%.`,
  };
};

// ── the generated headline / lede / tag-subhead for the preflop step ─────────────────────────────────────

export const preflopHeadline = (
  s: PreflopSnapshot,
  _model: LiveHandModel,
): { kicker: string; h2: string; lede: string; tagSub: string } => {
  const n = raisesIn(s);
  const hero = s.state.players.find((p) => p.seat === s.state.spec.heroSeat)!;
  const heroPos = POSITION_LABEL[hero.position];
  const limpers = s.state.history.filter(
    (h) => h.street === 'preflop' && h.action.kind === 'call' && h.action.seat !== s.state.spec.heroSeat,
  ).length;

  let h2: string;
  let lede: string;
  if (n === 0) {
    h2 = limpers > 0 ? `Limped to you on the ${heroPos}` : `Folded to you on the ${heroPos}`;
    lede =
      limpers > 0
        ? 'A limper on a loose table. Read the seat, then decide how to play your two cards.'
        : 'You are first to raise. Read the seats, then choose your line.';
  } else {
    // the man who made the raise hero is looking at - NOT `model.villainName`, which is the hand's read
    // subject and is a different player the moment someone comes over the top of hero behind the opener.
    // `villainArchetypeId` is resolved per node from the live state (liveRunner.villainHeroFaces).
    h2 = `${displayName(s.villainArchetypeId)} ${facingVerb(n)} to ${money(s.state.currentBet)}`;
    lede =
      n === 1
        ? `You are on the ${heroPos} facing a raise. Read the opener, then decide: fold, call, or re-raise.`
        : `You are on the ${heroPos} and he has come back over the top. Read what ${facing(n)} means from him, then decide: fold, call, or put it in again.`;
  }
  return {
    kicker: 'Preflop',
    h2,
    lede,
    // TB5: the read attaches to the villain hero faces AT THIS node (per-node `villainArchetypeId`), the SAME
    // man the felt headline names and Axis B grades against - NOT `model.villainName`, the hand-level subject
    // that is a different player the moment someone 4-bets over the top of hero's 3-bet.
    tagSub: `The main opponent is ${displayName(s.villainArchetypeId)}. What kind of player is he?`,
  };
};

// ── the two-axis preflop grade (PreflopDecisionGrade → ResolvedGrade for <GradeReveal>) ──────────────────

/** Move-class severity for the book-deviation demotion below (best is least severe). */
const SEVERITY: Record<MoveClass, number> = { best: 0, great: 1, exc: 2, book: 2, inacc: 3, mist: 4, blun: 5 };
const worse = (a: MoveClass, b: MoveClass): MoveClass => (SEVERITY[a] >= SEVERITY[b] ? a : b);

const INTENT_LABEL: Record<PreflopIntent, string> = {
  fold: 'Fold',
  limp: 'Limp',
  call: 'Call',
  open: 'Open',
  three_bet: '3-bet',
  four_bet: '4-bet',
  jam: 'All in',
};

/** Map a chosen ramp action + the node role to the PreflopIntent the PF2 grader consumes. */
export const intentFromRamp = (action: ActionKind, s: PreflopSnapshot): PreflopIntent => {
  const role = s.recommendation.role;
  switch (action) {
    case 'fold':
      return 'fold';
    case 'jam':
      return 'jam';
    case 'check':
      return 'call'; // a BB check (rare on the Button) - treat as taking the flop, closest to a call.
    case 'call':
      return role === 'open' ? 'limp' : 'call';
    case 'bet':
    case 'raise':
      return role === 'open' ? 'open' : role === 'defend' ? 'three_bet' : role === 'vs3bet' ? 'four_bet' : 'jam';
  }
};

/** Is this intent a RAISE of some depth? Those are the ones whose name depends on the pot, not the chart. */
const isRaiseIntent = (i: PreflopIntent): boolean => i === 'open' || i === 'three_bet' || i === 'four_bet';

/** Display label for a chosen/recommended intent AT THIS NODE.
 *
 *  The grader's vocabulary and the table's vocabulary diverge here, and the user gets the table's. PF2 grades
 *  hero's re-raise as `three_bet` at every 'defend' node - correct, that is the chart it is resolved against
 *  - but if he 3-bet, got 4-bet, and put it in again, the screen must not call that a "3-bet". `raiseFor`
 *  reads the actual raise depth; non-raise intents (fold/call/limp/jam) mean the same thing at any depth. */
const intentLabelAt = (intent: PreflopIntent, s: PreflopSnapshot): string =>
  isRaiseIntent(intent) ? raiseFor(raisesIn(s)).label : INTENT_LABEL[intent];

/** The engine's recommended line as a display label ("Open to $9", "4-bet to $144", "Fold"). */
const bookLineLabel = (rec: PreflopRecommendation, s: PreflopSnapshot): string => {
  const bb = s.state.spec.stakes.bb;
  const base = intentLabelAt(rec.finalIntent, s);
  if (rec.sizing && isRaiseIntent(rec.finalIntent)) {
    return `${base} to ${money(Math.round(rec.sizing.toBB * bb))}`;
  }
  return base;
};

/** Build the $0 preflop coach text from the recommendation the engine already produced - its target
 *  statement, the principles of any fired exploit rules, the limp-leak note, or a chart-membership fallback.
 *  No API call, no invented strategy (North Star §4.1: the engine answers, the coach only explains). */
export const preflopCoachText = (rec: PreflopRecommendation): string => {
  const parts: string[] = [];
  if (rec.targetStatement) parts.push(rec.targetStatement);
  // ONLY rules the engine actually acted on may speak. A rule flagged `suppressed` matched the spot but had
  // its `action.set` refused (an iso rule shouting "raise the limpers" over a hand the chart folds), so its
  // principle argues the opposite of `finalIntent`. Rendering it next to the target statement is what put
  // "fold: ..." and "size up for every limper" in the same paragraph.
  for (const r of rec.firedRules.filter((r) => !r.suppressed).slice(0, 2)) parts.push(r.principle);
  if (rec.isLeak) parts.push('Open-limping is legal here but plays as a leak - raising is the stronger default.');
  if (parts.length === 0) {
    parts.push(
      rec.inChart
        ? `Your hand is a standard ${INTENT_LABEL[rec.finalIntent].toLowerCase()} from this seat.`
        : `Your hand is outside the range here - the disciplined line is to ${INTENT_LABEL[rec.finalIntent].toLowerCase()}.`,
    );
  }
  // Terminal punctuation, then a space: these are independent sentences from independent authors (the
  // engine's target statement, a rule's principle), and a bare join(' ') ran them together mid-thought.
  return parts.map((p) => (/[.!?]$/.test(p.trim()) ? p.trim() : `${p.trim()}.`)).join(' ');
};

/** Build the full two-axis reveal for a committed preflop decision. Axis A is PF2's chart+EV grade of the
 *  intent (band → move class); Axis B is the read grade (tag vs the villain's real archetype, the SAME
 *  logic as postflop); the verdict combines them; the coach callout draws on the real recommendation. */
export const buildPreflopGrade = (
  s: PreflopSnapshot,
  tag: PlayerTag | null,
  action: ActionKind,
  amount: string | undefined,
): ResolvedGrade => {
  const intent = intentFromRamp(action, s);
  const grade = s.grade({ intent });

  const matchedBook = intent === s.recommendation.finalIntent;
  // `alternatives` is the list of OTHER options at this node, not a list of endorsements - `alternativesFor`
  // (strategy/preflop.ts) generates it mechanically, and when the book line is fold the only entry it offers
  // is the play the chart just rejected (`{intent:'open', note:'out of the opening chart'}`). Reading that
  // entry as a sanction is what graded J5o's off-chart button open "Best": sanctioned ⇒ no deviation floor,
  // and the bare band said `excellent` because the live path injects no equity, so estimatePreflopEv's 50%
  // default (grading/preflop.ts) makes ANY raise look profitable.
  //
  // So: when the book line is FOLD, nothing else is sanctioned. This is the exact mirror of the rule below
  // it - folding a hand the chart plays is a deviation, and playing a hand the chart folds is equally one.
  // It also covers the defend node, where the fold-book alternatives (`call`, `three_bet`) carry no
  // off-chart note at all and no error code names them.
  const bookIsFold = s.recommendation.finalIntent === 'fold';
  // Belt and braces: the engine's own named preflop-discipline errors (OPENED_OUT_OF_RANGE,
  // CALLED_3BET_OOP_TRASH, BB_OVERFOLD_VS_BTN - derivePreflopErrorCodes). A play the engine has explicitly
  // named as an error is never sanctioned, whatever the alternatives list says.
  const disciplineError = grade.errorCodes.length > 0;
  // A fold is only "sanctioned" when the book line ITSELF is fold - the engine lists fold as an alternative
  // for every open, but folding a hand the chart plays is the passive give-up leak the trainer teaches
  // against (it flags open-limping the same way). Every other listed alternative stays sanctioned.
  const sanctioned =
    !disciplineError &&
    (matchedBook ||
      (!bookIsFold && s.recommendation.alternatives.some((a) => a.intent === intent && a.intent !== 'fold')));
  // Axis A is the engine's honest band (MOVE_CLASS_FROM_BAND) for a sanctioned play. For a play that
  // DEVIATES from the book line (e.g. folding a hand the chart opens), the coarse one-street EV can grade
  // leniently (equity is not injected in the live path), so a bare band would paint a deviation "excellent".
  // We floor a deviation at Inaccuracy so the color, the coaching, and the wrong-fold counterfactual are
  // coherent - never PROMOTED, only kept from looking better than a book deviation is. Same spirit as Solo's
  // price-miss demotion; it never invents strategy, it reflects that the user left the engine's answer.
  const aCls: MoveClass = sanctioned
    ? MOVE_CLASS_FROM_BAND[grade.gradeBand]
    : worse(MOVE_CLASS_FROM_BAND[grade.gradeBand], 'inacc');
  const actionGood = sanctioned && SEVERITY[aCls] <= 2;

  const read = gradeReadAxis(tag, s.villainArchetypeId);
  const coachText = preflopCoachText(s.recommendation);
  const book = bookLineLabel(s.recommendation, s);

  const good = actionGood && read.right;
  const verdictCls = good ? 'best' : actionGood || read.right ? 'inacc' : 'blun';
  const verdictLabel = good
    ? 'Well read, well played'
    : actionGood
      ? 'Right play, read a little off'
      : read.right
        ? 'Right read, the play slipped'
        : 'Read and play both off';
  const mark = good ? '✓' : verdictCls === 'inacc' ? '!' : '✕';

  // Axis A text: the coach, plus an honest EV/adherence note. A modeled role (open/defend) gets the bb
  // cost; an abstained role (vs3bet/vs4bet/vsjam) says so - never a fabricated one-street number (PF2).
  const evNote =
    grade.abstained
      ? ' Graded on chart adherence - this spot is multi-street, so there is no honest one-street price.'
      : grade.evLossBB && grade.evLossBB > 0.5
        ? ` Costs about ${grade.evLossBB.toFixed(1)}bb versus the best line.`
        : '';

  const callout: ResolvedGrade['coach'] = actionGood
    ? { lead: 'Carry this forward.', text: coachText, bad: false }
    : matchedBook
      ? { lead: 'A thin spot.', text: `A defensible line the chart sanctions; the edge is just slim. ${coachText}`, bad: false }
      : { lead: 'Where it slipped.', text: `The book line here is ${book}. ${coachText}`, bad: true };

  return {
    verdict: {
      cls: verdictCls,
      mark,
      label: verdictLabel,
      sub: 'Graded on the read and the play, not on whether you won.',
    },
    axisA: {
      badge: 'A',
      kicker: 'Your play, preflop',
      cls: aCls,
      label: `${intentLabelAt(intent, s)}${amount && isRaiseIntent(intent) ? ` to $${amount}` : ''}`,
      text: `${coachText}${evNote}`,
    },
    axisB: {
      badge: 'B',
      kicker: 'Your read, the game',
      cls: read.cls,
      label: read.label,
      text: read.text,
    },
    coach: callout,
    debrief: {
      title: 'The read behind it',
      body: coachText,
      example: { label: 'Book line:', text: book },
    },
  };
};

// re-export for the surface's committed-action label (shared with the postflop path).
export { actionLabel };
